import React from "react";
import { Helmet } from 'react-helmet';
import Hero from './hero';
import Why from './why';
import Animation from './animation';
import Tabs from './tabs';
import FeaturedCaseStudy from './featured_case_study';
import ForYou from './for_you';
import Pricing from './pricing';
import FAQ from './faq';
import ReviewsCarousel from "../includes/reviewsCarousel";

const pmoServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.partneredmg.com/pmo-services#service",
    "name": "ReadyPMO — PMO as a Service",
    "description": "A turnkey Project Management Office stood up in 4–6 weeks, tailored to your organization without traditional consulting overhead. Includes custom workflows, dashboards, and one week of adoption support.",
    "provider": { "@id": "https://www.partneredmg.com/#organization" },
    "serviceType": "PMO Setup and Implementation",
    "areaServed": { "@type": "Country", "name": "United States" },
    "url": "https://www.partneredmg.com/pmo-services",
    "offers": {
        "@type": "Offer",
        "priceCurrency": "USD",
        "priceRange": "$4,600+/month",
        "description": "Fractional PMO management retainer starting at $4,600/month. Full PMO setup completed in 4–6 weeks. Month-to-month after 2-month minimum. Custom project pricing available.",
        "seller": { "@id": "https://www.partneredmg.com/#organization" }
    }
};

const PMOServices = () => {
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.partneredmg.com/"
            },
            {
                "@type": "ListItem",
                "position": 2,
                "name": "PMO Services",
                "item": "https://www.partneredmg.com/pmo-services"
            }
        ]
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": "How long does PMO implementation take?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Most organizations complete implementation within 4 to 6 weeks. The timeline depends on the size of your team and the level of structure already in place. We move at a pace that gets you results quickly while ensuring everything is set up the right way."
                }
            },
            {
                "@type": "Question",
                "name": "What support do I get after PMO launch?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "You will receive one week of adoption support and dashboard tuning after your PMO is set up. If you would like continued guidance, our Partnered Advisory package provides three months of dedicated support that includes coaching, process refinement, and ongoing systems and workflow adjustments."
                }
            },
            {
                "@type": "Question",
                "name": "How much customization is possible with PMG's PMO services?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Every implementation is tailored to your organization. We use industry best practices as the foundation, but the workflows, templates, and reporting are customized to match how your teams operate and what your business needs."
                }
            },
            {
                "@type": "Question",
                "name": "Who is ReadyPMO best suited for?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "ReadyPMO is designed for organizations that are growing quickly and need more structure around how work gets managed. It is a strong fit for mid-sized businesses, teams without a formal PMO, and organizations that are adopting project management software but struggling to use it effectively."
                }
            }
        ]
    };

    return (
        <div className="pt-24">
            <Helmet>
                <title>PMO as a Service | Fractional PMO Setup in 4–6 Weeks | PMG</title>
                <meta name="description" content="PMG's ReadyPMO service stands up a fully customized Project Management Office in 4–6 weeks — without traditional consulting overhead. Tailored workflows, dashboards, and adoption support included." />
                <link rel="canonical" href="https://www.partneredmg.com/pmo-services" />
                <meta property="og:title" content="PMO as a Service | Fractional PMO in 4–6 Weeks | PMG" />
                <meta property="og:description" content="Stand up a fully customized PMO in 4–6 weeks. PMG's ReadyPMO service delivers structured project management infrastructure without traditional consulting overhead." />
                <meta property="og:url" content="https://www.partneredmg.com/pmo-services" />
                <meta property="og:type" content="website" />
                <script type="application/ld+json">{JSON.stringify(pmoServiceSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
            </Helmet>
            <Hero />
            <Why />
            <Animation />
            <Tabs />
            <FeaturedCaseStudy />
            <ForYou />
            <Pricing />
            <FAQ />
            <ReviewsCarousel />
        </div>
    );
}

export default PMOServices;